/**
 * PATH: packages/email-hub/src/feature/components/BoardPeekPanel.tsx
 * FILE_ID: EMAILHUB-BOARD-PEEK-001
 * PURPOSE: Peek-first board panel for the reader's board-name chip and
 *          the `B` shortcut. Shows the allocated board's lanes with card
 *          counts, highlighting the lane THIS email's card sits on; the
 *          footer "Open board" does the full navigation the chip used to
 *          do on first click.
 * WHY:     Triage v5 (operator 2026-07-07): a board-chip click is mostly
 *          "did it file to the right lane?" - a lane list answers that
 *          without ejecting the operator from the inbox.
 * FLOW:
 *   ┌──────────────────┐   ┌───────────────────────┐   ┌───────────────┐
 *   │ allocation       │ → │ listLanes(withCounts) │ → │ lanes + counts │
 *   │ summary + api    │   │ (['kanban-lanes',id]  │   │ current lane ● │
 *   │                  │   │ cache, shared)        │   │ Open board nav │
 *   └──────────────────┘   └───────────────────────┘   └───────────────┘
 * DEPENDENCIES:
 *   - @quotech/kanban-host useKanbanBoardLanes: the same lane fetch the
 *     allocate submenu uses (shared query key = warm cache both ways)
 *   - ./EntityPeekShell: the shared slide-over chrome
 * RELATED:
 *   - EmailPreviewPane.tsx (openAllocatedBoard now peeks first)
 *   - @quotech/kanban-host CardPeekDrawer (the card twin)
 */

import { Kanban, Loader2 } from 'lucide-react'
import { useKanbanBoardLanes } from '@quotech/kanban-host'
import type { ComponentProps } from 'react'
import type { AllocationChip } from '@quotech/kanban-host'
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

import { cn } from '../../_internal/lib/utils'
import { EntityPeekShell } from './EntityPeekShell'

type KanbanApiProp = ComponentProps<typeof AllocationChip>['api']

export interface BoardPeekPanelProps {
  summary: EmailAllocationSummary
  api: KanbanApiProp
  onClose: () => void
  /** Full navigation to the board (host owns the router). */
  onOpen: () => void
}

export function BoardPeekPanel({ summary, api, onClose, onOpen }: BoardPeekPanelProps) {
  const boardId = summary.board_id ?? null
  const { lanes, isLoading, isError } = useKanbanBoardLanes({
    // The hook wants the full KanbanApi; the chip prop type is the same
    // client object (getSamKanbanApi()) with optional feature methods.
    api: api as Parameters<typeof useKanbanBoardLanes>[0]['api'],
    boardId,
  })

  const active = lanes
    .filter((l) => !l.archived)
    .slice()
    .sort((a, b) => a.position - b.position)

  return (
    <EntityPeekShell
      title={summary.board_name || 'Kanban board'}
      subtitle={
        summary.lane_name
          ? `This email is on "${summary.lane_name}"`
          : 'Kanban board'
      }
      icon={<Kanban className="h-4 w-4 text-violet-500" aria-hidden />}
      onClose={onClose}
      onOpen={onOpen}
      openLabel="Open board"
    >
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading lanes…
        </div>
      ) : isError ? (
        <div className="text-sm text-muted-foreground py-4">
          Could not load the lane list. "Open board" still works.
        </div>
      ) : (
        <div className="space-y-1" data-testid="board-peek-lanes">
          {active.map((lane) => {
            const isCurrent = lane.id === summary.lane_id
            return (
              <div
                key={lane.id}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                  isCurrent
                    ? 'bg-violet-500/10 border border-violet-300/50 dark:border-violet-500/30'
                    : 'border border-transparent',
                )}
                data-current={isCurrent || undefined}
              >
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ background: lane.color || 'var(--border)' }}
                  aria-hidden
                />
                <span
                  className={cn(
                    'truncate',
                    isCurrent ? 'font-medium text-foreground' : 'text-foreground/80',
                  )}
                >
                  {lane.name}
                </span>
                {isCurrent && (
                  <span className="text-[10px] text-violet-600 dark:text-violet-300 shrink-0">
                    this email
                  </span>
                )}
                {typeof lane.card_count === 'number' && (
                  <span className="ml-auto text-[11px] text-muted-foreground tabular-nums shrink-0">
                    {lane.card_count.toLocaleString()}
                  </span>
                )}
              </div>
            )
          })}
          {active.length === 0 && (
            <div className="text-sm text-muted-foreground py-4">This board has no lanes.</div>
          )}
        </div>
      )}
    </EntityPeekShell>
  )
}

export default BoardPeekPanel
